/**
 * Contribution Filing Certificate
 * Phase 2 document for governance records
 * No signatures required
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateFilingCertificateHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
    }
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0;
      border: 2px solid #c9a961;
      padding: 1in;
    }
    .header {
      text-align: center;
      border-bottom: 3px double #c9a961;
      padding-bottom: 18pt;
      margin-bottom: 20pt;
    }
    h1 {
      font-size: 18pt;
      margin: 0 0 8pt 0;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.12em;
    }
    .subtitle {
      font-size: 10pt;
      font-style: italic;
      color: #666;
    }
    .certificate-body {
      text-align: justify;
      margin: 20pt 0;
      line-height: 1.85;
    }
    .details-box {
      border: 1px solid #ccc;
      padding: 16pt;
      margin: 20pt 0;
      background-color: #fafafa;
      border-radius: 4pt;
    }
    .detail-row {
      margin-bottom: 8pt;
    }
    .detail-label {
      font-weight: bold;
      display: inline-block;
      min-width: 2.5in;
    }
    .seal {
      text-align: center;
      margin-top: 30pt;
      padding-top: 20pt;
      border-top: 1px solid #ccc;
    }
    .seal-text {
      font-family: 'Brush Script MT', cursive, serif;
      font-size: 16pt;
      font-style: italic;
      color: #c9a961;
    }
    .footer {
      margin-top: 24pt;
      font-size: 9pt;
      text-align: center;
      color: #666;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Contribution Filing Certificate</h1>
    <div class="subtitle">Official Ecclesiastical Record of Private Member Contribution</div>
  </div>
  
  <div class="certificate-body">
    This certificate hereby documents that on <strong>{{contribution_date}}</strong>, 
    <strong>{{contributor_name}}</strong>, acting in the capacity of {{contributor_role}} 
    and as a member of <strong>{{una_name}}</strong>, did make a private member contribution 
    within the ecclesiastical jurisdiction of the said unincorporated nonprofit association.
  </div>
  
  <div class="details-box">
    <div class="detail-row">
      <span class="detail-label">Contributor Name:</span> {{contributor_name}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Asset Type Contributed:</span> {{asset_type}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Contribution Method:</span> {{contribution_method}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Receiving Entity:</span> {{receiving_entity}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Contribution Date:</span> {{contribution_date}}
    </div>
    ${getAssetDetails(workflowData)}
  </div>
  
  <div class="certificate-body">
    This contribution was made voluntarily and without compulsion, within the private 
    ecclesiastical jurisdiction of {{una_name}}, and is not subject to public law gift, 
    donation, or transfer rules. The contribution has been formally authorized by the 
    contributor through a Resolution Regarding Authorization of Contribution, and has been 
    formally accepted by {{receiving_entity}} through a Resolution of Acceptance, both 
    documents being preserved in the ecclesiastical records of {{una_name}}.
  </div>
  
  <div class="certificate-body">
    The contributed asset shall be held and stewarded by {{receiving_entity}} in accordance 
    with the sacred purposes and governing documents of {{una_name}}, for the benefit of 
    the membership and the advancement of the UNA's mission.
  </div>
  
  <div class="seal">
    <div class="seal-text">{{una_name}}</div>
    <div style="margin-top: 14pt; font-size: 10pt;">
      Ecclesiastical Records<br>
      {{una_state}}
    </div>
  </div>
  
  <div class="footer">
    This certificate prepared {{current_date}}<br>
    For ecclesiastical record-keeping purposes<br>
    Private Association - Not Subject to Public Recording
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}

function getAssetDetails(workflowData: any): string {
  const assetType = workflowData.asset_type;
  
  if (assetType?.includes('Business Entity')) {
    return `
    <div class="detail-row">
      <span class="detail-label">Entity Name:</span> {{entity_legal_name}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Entity Type:</span> {{entity_type}}
    </div>
    <div class="detail-row">
      <span class="detail-label">State of Formation:</span> {{entity_state_of_formation}}
    </div>
    `;
  } else if (assetType?.includes('Real Estate')) {
    return `
    <div class="detail-row">
      <span class="detail-label">Property Address:</span> {{property_address}}, {{property_city}}, {{property_state}} {{property_zip}}
    </div>
    <div class="detail-row">
      <span class="detail-label">County:</span> {{property_county}}
    </div>
    `;
  } else if (assetType?.includes('Vehicle')) {
    return `
    <div class="detail-row">
      <span class="detail-label">Vehicle Description:</span> {{vehicle_description}}
    </div>
    <div class="detail-row">
      <span class="detail-label">VIN:</span> {{vehicle_vin}}
    </div>
    `;
  } else if (assetType === 'Intellectual Property') {
    return `
    <div class="detail-row">
      <span class="detail-label">IP Type:</span> {{ip_type}}
    </div>
    `;
  } else if (assetType === 'Digital/Cryptocurrency') {
    return `
    <div class="detail-row">
      <span class="detail-label">Digital Asset Type:</span> {{digital_asset_type}}
    </div>
    `;
  }
  
  return '';
}
